import React,{useEffect} from 'react';
import {useLocation,useNavigate,useSearchParams} from 'react-router-dom';
import {
  cancelOrderCheckout,
  reconcileStripeOrder
} from '../../services/stripePaymentService';
import {useSupabaseData} from '../../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../../contexts/SupabaseAuthContext';

const getHashQuery=()=>{
  const hash=window.location.hash || '';

  if (!hash.includes('?')) return null;

  return new URLSearchParams(hash.split('?')[1]);
};

const wait=(milliseconds)=>new Promise((resolve)=>{
  window.setTimeout(resolve,milliseconds);
});

const reconcilePaidOrder=async(orderId)=>{
  let lastResult=null;

  for (let attempt=0;attempt<4;attempt+=1) {
    lastResult=await reconcileStripeOrder(orderId);

    if (lastResult?.success && !lastResult?.pending) {
      return lastResult;
    }

    if (attempt<3) {
      await wait(1500*(attempt+1));
    }
  }

  return lastResult;
};

const getOrdersPath=(paymentResult,orderId)=>{
  const query=new URLSearchParams({
    payment:paymentResult,
    order:orderId
  });

  return `/orders?${query.toString()}`;
};

const CheckoutReturnReconciler=()=>{
  const {user}=useSupabaseAuth();
  const {refreshData}=useSupabaseData();
  const location=useLocation();
  const navigate=useNavigate();
  const [searchParams]=useSearchParams();

  useEffect(()=>{
    if (!user) return;

    const hashParams=getHashQuery();
    const paymentResult=searchParams.get('payment')
      || hashParams?.get('payment');
    const orderId=searchParams.get('order')
      || hashParams?.get('order');

    if (!paymentResult || !orderId) return;

    const storageKey=`checkout-${paymentResult}-reconciled-${orderId}`;
    const startedKey=`checkout-${paymentResult}-started-${orderId}`;

    if (window.sessionStorage.getItem(storageKey)==='true') {
      return;
    }

    if (window.sessionStorage.getItem(startedKey)==='true') {
      return;
    }

    window.sessionStorage.setItem(startedKey,'true');

    const ordersPath=getOrdersPath(paymentResult,orderId);

    // Return to My Orders immediately so the confirmation message is visible
    // while the server finishes reconciling the Stripe payment in the background.
    navigate(ordersPath,{replace:true});

    const reconcile=paymentResult==='cancelled'
      ? cancelOrderCheckout(orderId)
      : reconcilePaidOrder(orderId);

    reconcile
      .then(async(result)=>{
        if (!result?.success && !result?.pending) {
          throw new Error(
            result?.error || 'Stripe order reconciliation failed.'
          );
        }

        if (paymentResult==='cancelled' || !result?.pending) {
          window.sessionStorage.setItem(storageKey,'true');
        }

        window.dispatchEvent(new Event('sohlocal:orders-changed'));
        await refreshData();
      })
      .catch((error)=>{
        console.error(
          paymentResult==='cancelled'
            ? 'Unpaid checkout cancellation reconciliation failed:'
            : 'Paid checkout order reconciliation failed:',
          error
        );
        window.sessionStorage.removeItem(storageKey);
      })
      .finally(async()=>{
        await refreshData();
      });
  },[
    location,
    navigate,
    refreshData,
    searchParams,
    user
  ]);

  return null;
};

export default CheckoutReturnReconciler;
